import React from "react";
import { useTranslation } from "react-i18next";
import {
  CABINET_DRAWER_COUNTS,
  CABINET_HANDLE_STYLES,
  CabinetDrawerCount,
  CabinetHandleStyle,
} from "../../../../features/floorplan3d/symbols/primitives/kitchen/cabinet3DFront";

type CabinetFrontOptionGridProps = {
  value: CabinetDrawerCount | "";
  mixedLabel: string;
  onChange: (count: CabinetDrawerCount) => void;
};

type CabinetHandleOptionGridProps = {
  value: CabinetHandleStyle | "";
  mixedLabel: string;
  onChange: (style: CabinetHandleStyle) => void;
};

const optionButtonClass = (selected: boolean) =>
  [
    "flex flex-col items-center gap-1 rounded border p-1.5 text-[11px] leading-tight transition-colors",
    selected
      ? "border-blue-600 bg-blue-50 text-blue-900"
      : "border-gray-300 bg-white text-gray-700 hover:border-gray-400",
  ].join(" ");

/** Tiny SVG preview: door with vertical handle, or stacked drawers with top bars. */
const CabinetFrontThumb: React.FC<{ count: CabinetDrawerCount }> = ({
  count,
}) => {
  if (count === 0) {
    return (
      <svg viewBox="0 0 40 48" className="h-10 w-8" aria-hidden>
        <rect
          x="6"
          y="4"
          width="28"
          height="40"
          rx="1"
          fill="#c4b5a0"
          stroke="#8a7a66"
        />
        <rect
          x="28"
          y="14"
          width="3"
          height="18"
          rx="1"
          fill="#b0b4b8"
          stroke="#7a7e82"
        />
      </svg>
    );
  }

  const gap = 1.2;
  const top = 4;
  const bottom = 44;
  const usable = bottom - top - gap * (count - 1);
  const drawerHeight = usable / count;

  return (
    <svg viewBox="0 0 40 48" className="h-10 w-8" aria-hidden>
      {Array.from({ length: count }, (_, index) => {
        const y = bottom - (index + 1) * drawerHeight - index * gap;
        const handleY = y + 2.2;
        return (
          <g key={index}>
            <rect
              x="6"
              y={y}
              width="28"
              height={drawerHeight}
              rx="0.8"
              fill="#c4b5a0"
              stroke="#8a7a66"
            />
            <rect
              x="10"
              y={handleY}
              width="20"
              height="2"
              rx="1"
              fill="#b0b4b8"
              stroke="#7a7e82"
            />
          </g>
        );
      })}
    </svg>
  );
};

/** Tiny SVG preview for drawer handle length. */
const CabinetHandleThumb: React.FC<{ style: CabinetHandleStyle }> = ({
  style,
}) => {
  const handleWidth = style === "short" ? 10 : 20;
  const handleX = 20 - handleWidth / 2;
  return (
    <svg viewBox="0 0 40 48" className="h-10 w-8" aria-hidden>
      <rect
        x="6"
        y="10"
        width="28"
        height="12"
        rx="0.8"
        fill="#c4b5a0"
        stroke="#8a7a66"
      />
      <rect
        x="6"
        y="24"
        width="28"
        height="12"
        rx="0.8"
        fill="#c4b5a0"
        stroke="#8a7a66"
      />
      {style !== "none" && (
        <>
          <rect
            x={handleX}
            y="12.2"
            width={handleWidth}
            height="2"
            rx="1"
            fill="#b0b4b8"
            stroke="#7a7e82"
          />
          <rect
            x={handleX}
            y="26.2"
            width={handleWidth}
            height="2"
            rx="1"
            fill="#b0b4b8"
            stroke="#7a7e82"
          />
        </>
      )}
    </svg>
  );
};

const optionLabelKey = (count: CabinetDrawerCount) =>
  count === 0
    ? "propertyPanel.cabinetFrontStyle.door"
    : `propertyPanel.cabinetFrontStyle.drawers${count}`;

/**
 * Compact thumbnail grid for Cabinet5 front: door or 1–5 drawers.
 */
export const CabinetFrontOptionGrid: React.FC<CabinetFrontOptionGridProps> = ({
  value,
  mixedLabel,
  onChange,
}) => {
  const { t } = useTranslation();
  return (
    <div className="px-3 pb-3">
      {value === "" && (
        <div className="mb-1 text-[11px] text-gray-500">{mixedLabel}</div>
      )}
      <div
        className="grid grid-cols-3 gap-2"
        role="listbox"
        aria-label={t("propertyPanel.cabinetFront")}
      >
        {CABINET_DRAWER_COUNTS.map((count) => {
          const selected = value === count;
          return (
            <button
              key={count}
              type="button"
              role="option"
              aria-selected={selected}
              aria-label={t(optionLabelKey(count))}
              className={optionButtonClass(selected)}
              onClick={() => onChange(count)}
            >
              <CabinetFrontThumb count={count} />
              <span className="truncate">{t(optionLabelKey(count))}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Compact thumbnail grid for Cabinet5 drawer handles: long / short / none.
 */
export const CabinetHandleOptionGrid: React.FC<CabinetHandleOptionGridProps> = ({
  value,
  mixedLabel,
  onChange,
}) => {
  const { t } = useTranslation();
  return (
    <div className="px-3 pb-3">
      {value === "" && (
        <div className="mb-1 text-[11px] text-gray-500">{mixedLabel}</div>
      )}
      <div
        className="grid grid-cols-3 gap-2"
        role="listbox"
        aria-label={t("propertyPanel.cabinetHandles")}
      >
        {CABINET_HANDLE_STYLES.map((style) => {
          const selected = value === style;
          return (
            <button
              key={style}
              type="button"
              role="option"
              aria-selected={selected}
              aria-label={t(`propertyPanel.cabinetHandleStyle.${style}`)}
              className={optionButtonClass(selected)}
              onClick={() => onChange(style)}
            >
              <CabinetHandleThumb style={style} />
              <span className="truncate">
                {t(`propertyPanel.cabinetHandleStyle.${style}`)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CabinetFrontOptionGrid;
